import _ from 'lodash'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ViewState, SchedulerDateTime } from '@devexpress/dx-react-scheduler'
import {
  Appointments,
  DateNavigator,
  DayView,
  MonthView,
  Scheduler,
  TodayButton,
  Toolbar,
  ViewSwitcher,
  WeekView,
  CurrentTimeIndicator,
} from '@devexpress/dx-react-scheduler-material-ui'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { Stack, useMediaQuery, useTheme } from '@mui/material'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import { useGetBusySlotsQuery, useGetSchedulesQuery } from '@/core/services/schedule'
import { useGetMeetingLinkOrdersQuery } from '@/core/services/order'
import { useGetMeetingLinksQuery } from '@/core/services/meeting-link'
import { MeetingLink, Unavailability } from '@/core/models/meeting-link.model'
import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { OrderStatus } from '@/core/models/order.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getConsultations, selectConsultations } from '@/redux/reducers/consultation.reducer'

import PendingOrdersQueue from './PendingOrdersQueue'
import AppointmentDetailPanel from './AppointmentDetailPanel'

import AddScheduleDialog from '../containers/AddScheduleDialog'
import AgendaAppointments from './AgendaAppointments'
import AgendaAppointmentTooltip from './AgendaAppointmentTooltip'
import AppointmentForm from './AppointmentForm'
import TimeTableCell from './TimeTableCell'
import TimeIndicator from './TimeIndicator'
import { useSchedulerData } from '../hooks/useSchedulerData'

export enum AgendaView {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

type AgendaSchedulerProps = {
  staffs: string[]
  targetUserId?: string
  height?: number | 'auto'
  renderDrawer?: (props: {
    staffs: string[]
    staffScheduleColors: Record<string, string>
    staffAppointmentVisibilities: Record<string, boolean>
    setStaffs: React.Dispatch<React.SetStateAction<string[]>>
    setStaffScheduleColors: React.Dispatch<React.SetStateAction<Record<string, string>>>
    setStaffAppointmentVisibilities: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
    orderStatusFilter: OrderStatus[]
    onOrderStatusFilterChange: (statuses: OrderStatus[]) => void
    meetingLinkFilter: string[]
    onMeetingLinkFilterChange: (meetingLinkIds: string[]) => void
    meetingLinks: MeetingLink[] | undefined
  }) => React.ReactNode
  cellDuration?: number
  defaultView?: AgendaView
  disableSchedules?: boolean
  disableViewSwitcher?: boolean
  startDayHour?: number
  endDayHour?: number
}

export function AgendaScheduler(props: AgendaSchedulerProps) {
  const {
    staffs: initialStaffs,
    targetUserId,
    height = 'auto',
    renderDrawer,
    cellDuration,
    defaultView = AgendaView.WEEK,
    disableSchedules = false,
    disableViewSwitcher = false,
    startDayHour = 6,
    endDayHour = 20,
  } = props

  const dispatch = useAppDispatch()
  const { i18n, t } = useTranslation()

  const [currentDate, setCurrentDate] = useState<SchedulerDateTime>(new Date())
  const [currentViewName, setCurrentViewName] = useState<string>(defaultView)

  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatus[]>([])
  const [meetingLinkFilter, setMeetingLinkFilter] = useState<string[]>([])
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)

  const authUser = useAppSelector(selectAuthUser)
  const consultations = useAppSelector(selectConsultations)

  const { data: meetingLinkOrders } = useGetMeetingLinkOrdersQuery({ acceptedBy: authUser?._id }, { skip: !authUser, pollingInterval: 30000 })
  const { data: meetingLinks } = useGetMeetingLinksQuery({ staffs: [authUser?._id ?? ''] }, { skip: !authUser })

  // State for drawer control, initialized with props but can be managed internally if drawer is present
  const [staffs, setStaffs] = useState<string[]>(initialStaffs)
  const [staffScheduleColors, setStaffScheduleColors] = useState<Record<string, string>>(authUser ? { [authUser._id]: '#64b5f6' } : {})
  const [staffAppointmentVisibilities, setStaffAppointmentVisibilities] = useState<Record<string, boolean>>(authUser ? { [authUser._id]: true } : {})

  // Update internal staffs state when prop changes
  useEffect(() => {
    setStaffs(initialStaffs)

    // If no drawer is present (which usually manages these), we should ensure the passed staffs are visible
    if (!renderDrawer) {
      const newColors: Record<string, string> = {}
      const newVisibilities: Record<string, boolean> = {}

      initialStaffs.forEach((staffId) => {
        newColors[staffId] = '#64b5f6' // Default color
        newVisibilities[staffId] = true
      })

      setStaffScheduleColors(newColors)
      setStaffAppointmentVisibilities(newVisibilities)
    }
  }, [initialStaffs, renderDrawer])

  const { data: busySlots } = useGetBusySlotsQuery({ owners: staffs }, { refetchOnMountOrArgChange: true, skip: disableSchedules })
  const { data: schedules } = useGetSchedulesQuery({ createdBy: staffs }, { refetchOnMountOrArgChange: true, skip: disableSchedules })

  const allUnavailabilities = useMemo(() => {
    if (!meetingLinks) return []
    return meetingLinks.flatMap((ml) => (ml.availabilities || []).flatMap((a) => a.unavailabilities || []))
  }, [meetingLinks])

  const schedulerData = useSchedulerData(
    { busySlots, consultations, orders: meetingLinkOrders, orderStatusFilter, meetingLinkFilter, schedules, unavailabilities: allUnavailabilities },
    currentDate,
    {
      scheduleColors: staffScheduleColors,
      appointmentVisibilities: staffAppointmentVisibilities,
    }
  )

  const selectedOrder = meetingLinkOrders?.find((order) => order._id === selectedOrderId) ?? null

  useEffect(() => {
    dispatch(
      getConsultations({
        startDate: {
          $gte: dayjs().startOf('week').toISOString(),
          $lte: dayjs().endOf('week').toISOString(),
        },
      })
    )
  }, [dispatch])

  const handleCurrentDateChange = (newCurrentDate: Date) => {
    setCurrentDate(newCurrentDate)
    dispatch(
      getConsultations({
        startDate: {
          $gte: dayjs(newCurrentDate)
            .startOf(currentViewName as dayjs.OpUnitType)
            .toISOString(),
          $lte: dayjs(newCurrentDate)
            .endOf(currentViewName as dayjs.OpUnitType)
            .toISOString(),
        },
      })
    )
  }

  const handleCurrentViewNameChange = useCallback(
    (viewName: string) => {
      setCurrentViewName(viewName)
      dispatch(
        getConsultations({
          startDate: {
            $gte: dayjs(currentDate)
              .startOf(viewName as dayjs.OpUnitType)
              .toISOString(),
            $lte: dayjs(currentDate)
              .endOf(viewName as dayjs.OpUnitType)
              .toISOString(),
          },
        })
      )
    },
    [setCurrentViewName, dispatch, currentDate]
  )
  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (isMobile) {
      handleCurrentViewNameChange(AgendaView.DAY)
    }
  }, [isMobile, handleCurrentViewNameChange])

  return (
    <Stack direction="row" flex={1} sx={{ maxHeight: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }}>
      {renderDrawer &&
        renderDrawer({
          staffs,
          staffScheduleColors,
          staffAppointmentVisibilities,
          setStaffs,
          setStaffScheduleColors,
          setStaffAppointmentVisibilities,
          orderStatusFilter,
          onOrderStatusFilterChange: setOrderStatusFilter,
          meetingLinkFilter,
          onMeetingLinkFilterChange: setMeetingLinkFilter,
          meetingLinks,
        })}
      <Stack sx={{ flex: 1, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Paper sx={{ height: '100%' }}>
            <Scheduler data={schedulerData} locale={i18n.language} height={height}>
              <ViewState
                currentDate={currentDate}
                onCurrentDateChange={handleCurrentDateChange}
                currentViewName={currentViewName}
                onCurrentViewNameChange={handleCurrentViewNameChange}
              />
              <DayView
                cellDuration={cellDuration}
                name={AgendaView.DAY}
                displayName={t('page.agenda.view.day.displayName')}
                startDayHour={startDayHour}
                endDayHour={endDayHour}
                timeTableCellComponent={TimeTableCell.DayView}
              />
              <WeekView
                cellDuration={cellDuration}
                name={AgendaView.WEEK}
                displayName={t('page.agenda.view.week.displayName')}
                startDayHour={startDayHour}
                endDayHour={endDayHour}
                timeTableCellComponent={TimeTableCell.WeekView}
              />
              <MonthView name={AgendaView.MONTH} displayName={t('page.agenda.view.month.displayName')} timeTableCellComponent={TimeTableCell.MonthView} />
              <Toolbar />
              <DateNavigator />
              <TodayButton
                messages={{
                  today: t('page.agenda.todayButton.label'),
                }}
              />
              <AddScheduleDialog targetUserId={targetUserId} />
              {/* {!disableViewSwitcher && <ViewSwitcher />} */}
              <AgendaAppointments />
              <AgendaAppointmentTooltip />
              <AppointmentForm />
              <CurrentTimeIndicator indicatorComponent={TimeIndicator} shadePreviousAppointments shadePreviousCells />
            </Scheduler>
          </Paper>
        </Box>
        <PendingOrdersQueue orders={meetingLinkOrders} onOrderClick={setSelectedOrderId} />
      </Stack>
      <AppointmentDetailPanel order={selectedOrder} open={!!selectedOrderId} onClose={() => setSelectedOrderId(null)} />
    </Stack>
  )
}
